import { Alert, Box, Button, Card, Checkbox, Grid, Typography } from '@mui/material';
import { gridFilteredSortedRowIdsSelector, useGridApiRef } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { updateFacilityInvoiceVendorLineItem } from '../../../../api/invoicing';
import { updateFacilitySettingsInRedux } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { useSnackbar } from '../../../../reusable-components/snackbar';

LineItemSettings.propTypes = {
  gridSx: PropTypes.object,
  heightSx: PropTypes.object,
}

export default function LineItemSettings({ gridSx, heightSx }) {
  const { invoiceFacilitySettings } = useSelector((state) => state.invoicing.data);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [data, setData] = useState([]);

  const apiRef = useGridApiRef();

  const handleSave = async () => {
    await updateFacilityInvoiceVendorLineItem(data)
      .then((res) => {
        if (res.status === 200) {
          dispatch(updateFacilitySettingsInRedux(res));
          setData(res.data)
          enqueueSnackbar('Facility settings updated successfully', { variant: 'success' });
        } else {
          enqueueSnackbar('Failed to update facility settings', { variant: 'error' });
        }
      })
      .catch(() =>
        enqueueSnackbar('Failed to update facility settings', { variant: 'error' })
      );
  };

  const handleCancel = () => {
    setData(invoiceFacilitySettings);
  };


  const columns = [
    {
      field: 'facilityName',
      headerName: 'Facility',
      type: 'customText',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'company',
      headerName: 'Group',
      type: 'customText',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'vendorAsLineItem',
      headerName: 'Vendor',
      minWidth: 100,
      flex: 1,
      renderHeader: (params) => {
        if (!apiRef.current) {
          return (
            <>
              {params.colDef.headerName}
              <Checkbox disabled />
            </>
          );
        }
        const visibleRowIds = gridFilteredSortedRowIdsSelector(
          apiRef.current.state,
          apiRef.current.instanceId
        );
        const visibleRows = data.filter(row => visibleRowIds.includes(row.id));
        const allChecked = visibleRows.length > 0 && visibleRows.every(row => row.vendorAsLineItem);
        const someChecked = visibleRows.some(row => row.vendorAsLineItem) && !allChecked;

        return (
          <>
            {params.colDef.headerName}
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onClick={e => e.stopPropagation()}
              onChange={(e) => {
                const { checked } = e.target;
                setData(prev =>
                  prev.map(row =>
                    visibleRowIds.includes(row.id)
                      ? { ...row, vendorAsLineItem: checked }
                      : row
                  )
                );
              }}
            />
          </>
        );
      },
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={(e) => {
            const { checked } = e.target;
            setData(prev =>
              prev.map(row =>
                row.id === params.row.id ? { ...row, vendorAsLineItem: checked } : row
              )
            );
          }}
        />
      )
    }
  ];

  useEffect(() => {
    if (invoiceFacilitySettings && invoiceFacilitySettings.length > 0 && data.length === 0) {
      setData(invoiceFacilitySettings);
    }
  }, [invoiceFacilitySettings, data.length]);


  const invoiceFacilitySettingsTrimmed = invoiceFacilitySettings.map(s => ({ company: s.company, facilityName: s.facilityName, id: s.id, vendorAsLineItem: s.vendorAsLineItem }));
  const dataTrimmed = data.map(s => ({ company: s.company, facilityName: s.facilityName, id: s.id, vendorAsLineItem: s.vendorAsLineItem }));

  return (
    <>
      {data?.length > 0 && (
        <Card sx={{ p: 2, ...heightSx }}>
          <Typography variant="h5" color="primary.main">
            Add Vendor to Invoice Line Item
          </Typography>
          <Alert severity="info">
            <Typography fontSize={'0.875rem'} color="info.main">
              For Intacct only: this feature will add the vendor name to the intact vendor field.              
            </Typography>
          </Alert>
          <Box sx={{ maxHeight: '450px' }}>
            <CustomDataGrid
              data={data}
              gridColumns={columns}
              forceReRender={data.map(row => row.vendorAsLineItem).join(',')}
              gridId={'invoice-settings-tab-line-vendor'}
              hideFilterSettings
              boxSX={{ width: '100%', height: '450px' }}
              sx={gridSx}
              apiRef={apiRef}
            />
          </Box>
          {!_.isEqual(invoiceFacilitySettingsTrimmed, dataTrimmed) && (
            <Grid container justifyContent={'center'} paddingTop={2}>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                sx={{ width: 150, mr: 2 }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <AwaitButton
                variant="contained"
                color="primary"
                size="medium"
                sx={{ width: 150 }}
                onClick={handleSave}
              >
                Save
              </AwaitButton>
            </Grid>
          )}
        </Card>
      )}
    </>
  );
}